/**
 * Process-wide latch for "the frontend settings provider has finished its
 * initial load".
 *
 * `useSettings().isLoading` starts as `false` before the provider's load effect
 * runs, flips to `true` while fetching, then settles back to `false`. A consumer
 * that mounts *after* the fetch already settled (e.g. AppLayout rendered only
 * once the OpenDock auth redirect finishes) would see `false` from the start and
 * could not tell "not loaded yet" from "done loading".
 *
 * We resolve that by having an always-mounted probe report the loading flag into
 * this module-level store as it transitions. Once we have observed loading start
 * and then finish, the latch stays `true` for the lifetime of the tab, so any
 * later consumer reads the settled state immediately.
 */

let hasStartedLoading = false
let isReady = false
const listeners = new Set<() => void>()

export function observeSettingsLoading(isLoading: boolean): void {
  if (isLoading) {
    hasStartedLoading = true
    return
  }

  if (hasStartedLoading && !isReady) {
    isReady = true
    listeners.forEach(listener => listener())
  }
}

export function getSettingsReady(): boolean {
  return isReady
}

export function subscribeSettingsReady(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test-only: reset the latch between test cases. */
export function resetSettingsReadyStore(): void {
  hasStartedLoading = false
  isReady = false
  listeners.clear()
}
